import {client} from '@dokia/strapi/client';
import strapiQuerySerializer from '@dokia/strapi/query-serializer';

import {
  AuthSessionExpiredError,
  refreshAuthSessionAccessToken,
} from './authSession';

/**
 * Config Strapi mutualisée (browser + SSR/loaders).
 *
 * ⚠️ Ne pas renommer en strapi.client.ts : React Router exclut les fichiers
 * dont le nom contient ".client" du bundle serveur (client-only), ce qui
 * rendrait les exports undefined côté SSR.
 */

/**
 * URL de base de l'API Strapi (browser + Node/SSR).
 */
export function getStrapiBaseURL(): string {
  // Lors du build avec Vite, import.meta.env est la bonne source pour les
  // variables d'environnement
  // Cela fonctionne à la fois côté client et côté SSR/prerender
  const root = (import.meta.env.VITE_STRAPI_URL as string | undefined) ?? '';
  return root ? `${root}/api` : '';
}

// Configuration au chargement du module (browser + SSR)
client.setConfig({
  baseURL: getStrapiBaseURL(),
  querySerializer: strapiQuerySerializer,
});

let _accessToken: string | undefined;
const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

type RetryableRequest = {
  data?: unknown;
  headers: Record<string, string | undefined>;
  isAuthRetried?: boolean;
  method?: string;
  params?: unknown;
  url?: string;
};

export function setStrapiClientAccessToken(token: string | undefined): void {
  _accessToken = token;
}

client.instance.interceptors.request.use(config => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  } else {
    delete config.headers.Authorization;
  }
  return config;
});

client.instance.interceptors.response.use(
  async response => {
    await validateResponseAgainstSchema(
      response.config.url ?? '',
      response.data as unknown,
    );

    return response;
  },
  async (error: unknown) => {
    const originalRequest = getRetryableRequestFromError(error);
    if (!originalRequest) {
      throw error;
    }

    if (!shouldRetryUnauthorizedRequest(error, originalRequest)) {
      throw error;
    }

    try {
      const refreshedAccessToken = await refreshAuthSessionAccessToken({
        baseUrl: `${apiBase}/webapp`,
      });
      setStrapiClientAccessToken(refreshedAccessToken);
      originalRequest.headers.Authorization = `Bearer ${refreshedAccessToken}`;
      originalRequest.isAuthRetried = true;

      return await client.instance.request(originalRequest);
    } catch (refreshError) {
      if (refreshError instanceof AuthSessionExpiredError) {
        throw refreshError;
      }
      throw error;
    }
  },
);

function getErrorResponseStatus(error: unknown): number | undefined {
  if (!isRecord(error) || !('response' in error)) {
    return undefined;
  }

  const rawResponse = error.response;
  if (!isRecord(rawResponse) || typeof rawResponse.status !== 'number') {
    return undefined;
  }

  return rawResponse.status;
}

function getRetryableRequestFromError(
  error: unknown,
): RetryableRequest | undefined {
  if (!isRecord(error) || !('config' in error)) {
    return undefined;
  }

  const rawConfig = error.config;
  if (!isRecord(rawConfig)) {
    return undefined;
  }

  const rawHeaders = isRecord(rawConfig.headers) ? rawConfig.headers : {};
  const headers: Record<string, string | undefined> = {};
  for (const [headerName, headerValue] of Object.entries(rawHeaders)) {
    if (typeof headerValue === 'string') {
      headers[headerName] = headerValue;
    }
  }

  return {
    data: rawConfig.data,
    headers,
    isAuthRetried:
    typeof rawConfig.isAuthRetried === 'boolean'
      ? rawConfig.isAuthRetried
      : undefined,
    method: typeof rawConfig.method === 'string' ? rawConfig.method : undefined,
    params: rawConfig.params,
    url: typeof rawConfig.url === 'string' ? rawConfig.url : undefined,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isZodSchema(value: unknown): value is {
  safeParse: (data: unknown) => {success: boolean};
} {
  return isRecord(value) && typeof value.safeParse === 'function';
}

function readRecordValue(
  record: Record<string, unknown>,
  key: string,
): unknown {
  return record[key];
}

function shouldRetryUnauthorizedRequest(
  error: unknown,
  request: RetryableRequest,
): boolean {
  const isUnauthorized = getErrorResponseStatus(error) === 401;
  const hasAuthorizationHeader = Boolean(request.headers.Authorization);
  const isRetriedRequest = request.isAuthRetried === true;


  return isUnauthorized && hasAuthorizationHeader && !isRetriedRequest;
}

async function validateResponseAgainstSchema(
  url: string,
  data: unknown,
): Promise<void> {
  const zod = await import('@dokia/strapi/zod');

  const apiPath = url.split('/api/')[1]?.split('?')[0] ?? '';
  const parts = apiPath.split('/').filter(Boolean);
  const resource = parts[0];
  const isFirmAdminContact =
    resource === 'users' && parts[1] === 'firm-admin-contact';
  const isFirmMembers = resource === 'users' && parts[1] === 'firm-members';
  const isById = parts.length >= 2 && !isFirmMembers && !isFirmAdminContact;

  type ZodSchema = {safeParse: (data: unknown) =>
    {error?: {issues: unknown}; success: boolean;}};
  const rawInfoPopinSchema = isRecord(zod)
    ? readRecordValue(zod, 'zInfoPopinGetInfoPopinResponse')
    : undefined;
  const infoPopinSchema = isZodSchema(rawInfoPopinSchema)
    ? rawInfoPopinSchema
    : undefined;

  if (isFirmAdminContact) {
    const result = zod.zUserGetFirmAdminContactResponse.safeParse(data);
    if (!result.success) {
      console.error('[strapi] zod validation failed:',
        url,
        JSON.stringify(result.error.issues),
      );
    }
    return;
  }

  if (isFirmMembers) {
    const result = zod.zUserListFirmMembersResponse.safeParse(data);
    if (!result.success) {
      console.error('[strapi] zod validation failed:',
        url,
        JSON.stringify(result.error.issues),
      );
    }
    return;
  }

  const schemaMap: Record<string,
  {byId: ZodSchema; list: ZodSchema} | undefined> = {
    'business-pages': {
      byId: zod.zBusinessPageGetBusinessPagesByIdResponse,
      list: zod.zBusinessPageGetBusinessPagesResponse,
    },
    'firm-roles': {
      byId: zod.zFirmRoleGetFirmRolesByIdResponse,
      list: zod.zFirmRoleGetFirmRolesResponse,
    },
    'firms': {
      byId: zod.zFirmGetFirmsByIdResponse,
      list: zod.zFirmGetFirmsResponse,
    },
    'library-documents': {
      byId: zod.zLibraryDocumentGetLibraryDocumentsByIdResponse,
      list: zod.zLibraryDocumentGetLibraryDocumentsResponse,
    },
    'procedures': {
      byId: zod.zProcedureGetProceduresByIdResponse,
      list: zod.zProcedureGetProceduresResponse,
    },
    // Single type : pas de variante byId, même schéma dans les deux cas.
    'info-popin': infoPopinSchema
      ? {
        byId: infoPopinSchema,
        list: infoPopinSchema,
      }
      : undefined,
    'terms-and-conditions-version': {
      byId: zod.zTermsAndConditionsVersionGetTermsAndConditionsVersionResponse,
      list: zod.zTermsAndConditionsVersionGetTermsAndConditionsVersionResponse,
    },
    'themes': {
      byId: zod.zThemeGetThemesByIdResponse,
      list: zod.zThemeGetThemesResponse,
    },
    'users': {
      byId: zod.zUserGetUsersByIdResponse,
      list: zod.zUserGetUsersResponse,
    },
  };

  const schemas = schemaMap[resource];
  if (!schemas) {
    console.warn(
      '[strapi] no zod schema registered for resource',
      resource,
      ': skipping validation for', url,
    );
    return;
  }

  const schema = isById ? schemas.byId : schemas.list;
  const result = schema.safeParse(data);

  if (!result.success) {
    console.error('[strapi] zod validation failed:',
      url,
      JSON.stringify(result.error?.issues),
    );
  }
}

// eslint-disable-next-line unicorn/prefer-export-from
export {client};

